/* eslint-disable no-nested-ternary */

import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    UerCurrentEstimateBasisQuery,
    PriceListUerRowWithRatioData,
    ServiceDataResultOfPriceListUerRowBasisData,
    UerCreateUerRowMutation,
} from 'gql/graphql';
import { UerRecordStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { updateUerToActKC2FromPriceList } from 'pages/Project/UerProject/WorkOrder/workOrderQueries';
import type { TActionCommonInterface } from 'utils/types';

import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
    createUerRowGql,
    updateUerPriceListRow,
    updateUerToWorkOrderFromPriceListGql,
} from '../createOrEditUerQueries';
import { prepareAddUerData, prepareUpdateUerData } from '../prepareAddUer';

interface IUseSaveUer {
    uerId?: string;
    workOrderPriceId?: string;
    contractId?: number;
    isUerParsed: boolean;
    uerRecord?: PriceListUerRowWithRatioData | null;
    checkboxValues: Record<string, boolean>;
    reset: (data: Record<string, any>) => void;
    currentEstimateBasisData?: UerCurrentEstimateBasisQuery['uerCurrentEstimateBasis'];
    onCloseModal?: () => void;
    onSaveSuccess: () => void;
    onCloseCreateUerModal?: () => void;
    setCheckedIds?: Dispatch<SetStateAction<Record<string, boolean>>>;
    workOrderStatus?: WorkOrderStatusEnum;
    parsedUerData?: ServiceDataResultOfPriceListUerRowBasisData;
}

const updateUerPriceListRowRequestId = 'updateUerPriceListRow';
const createUerRowRequestId = 'createUerRow';
const updateUerToWorkOrderFromPriceListRequestId = 'updateUerToWorkOrderFromPriceList';
const updateUerToActKC2FromPriceListRequestId = 'updateUerToActKC2FromPriceList';

export const useSaveUer = (props: IUseSaveUer) => {
    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const {
        uerId,
        isUerParsed,
        uerRecord,
        checkboxValues,
        contractId,
        onSaveSuccess,
        onCloseModal,
        workOrderPriceId,
        onCloseCreateUerModal,
        setCheckedIds,
        currentEstimateBasisData,
        workOrderStatus,
        parsedUerData,
    } = props;
    const dispatch = useDispatch();

    const successUpdateHandler = useCallback(() => {
        onCloseModal?.();
        onSaveSuccess();
    }, [onCloseModal, onSaveSuccess]);

    // --- updateUerPriceListRow
    const [resetUpdateUerPriceListRowHandler] = useResetStoreData({ requestId: updateUerPriceListRowRequestId });

    const updateUerPriceListRowSuccessUpdated = useCallback(() => {
        successUpdateHandler();
        resetUpdateUerPriceListRowHandler(undefined);
    }, [resetUpdateUerPriceListRowHandler, successUpdateHandler]);

    const [, updateUerPriceListRowStatus, , updateUerPriceListRowRequest] = useRequest({
        requestId: updateUerPriceListRowRequestId,
        query: updateUerPriceListRow,
        withEffect: false,
        dependenciesUpdater: updateUerPriceListRowSuccessUpdated,
    });
    // ---

    // --- createUerRow
    const [resetCreateUerRowHandler] = useResetStoreData({ requestId: createUerRowRequestId });

    const createUerRowSuccessUpdated = useCallback(
        (depsVars: TActionCommonInterface<UerCreateUerRowMutation>) => {
            if (depsVars.payload.uerCreateUerRow.data?.id && setCheckedIds) {
                setCheckedIds((checkedIds) => ({ ...checkedIds, [depsVars.payload.uerCreateUerRow.data?.id]: true }));
            }

            onSaveSuccess();

            if (onCloseCreateUerModal) {
                onCloseCreateUerModal();
            }
        },
        [onCloseCreateUerModal, onSaveSuccess, setCheckedIds],
    );

    const [, createUerRowStatus, , createUerRowRequest] = useRequest<UerCreateUerRowMutation>({
        requestId: createUerRowRequestId,
        query: createUerRowGql,
        withEffect: false,
        dependenciesUpdater: createUerRowSuccessUpdated,
    });
    // ---

    // --- updateUerToWorkOrderFromPriceList
    const [resetUpdateUerToWorkOrderFromPriceList] = useResetStoreData({
        requestId: updateUerToWorkOrderFromPriceListRequestId,
    });

    const updateUerToWorkOrderFromPriceListSuccess = useCallback(() => {
        successUpdateHandler();
        resetUpdateUerToWorkOrderFromPriceList(undefined);
    }, [resetUpdateUerToWorkOrderFromPriceList, successUpdateHandler]);

    const [, updateUerToWorkOrderFromPriceListStatus, , updateUerToWorkOrderFromPriceListRequest] = useRequest({
        requestId: updateUerToWorkOrderFromPriceListRequestId,
        query: updateUerToWorkOrderFromPriceListGql,
        withEffect: false,
        dependenciesUpdater: updateUerToWorkOrderFromPriceListSuccess,
    });
    // ---

    // --- updateUerToActKC2FromPriceList
    const [resetUpdateUerToActKC2Data] = useResetStoreData({
        requestId: updateUerToActKC2FromPriceListRequestId,
    });

    const uerToActKC2SuccessUpdated = useCallback(() => {
        successUpdateHandler();
        resetUpdateUerToActKC2Data(undefined);
    }, [resetUpdateUerToActKC2Data, successUpdateHandler]);

    const [, updateUerToActKC2FromPriceListStatus, , updateUerToActKC2FromPriceListRequest] = useRequest({
        requestId: updateUerToActKC2FromPriceListRequestId,
        query: updateUerToActKC2FromPriceList,
        withEffect: false,
        dependenciesUpdater: uerToActKC2SuccessUpdated,
    });
    // ---

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: orderId ? Number(orderId) : undefined,
            orderStatus: projectData?.contractNavigation?.data?.workOrderNavs?.find((w) => w.id === Number(orderId))
                ?.status,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            orderId,
            projectData?.contractNavigation?.data?.workOrderNavs,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
        ],
    );

    const onUpdate = useCallback(
        (submitData: Record<string, any>) => {
            if (uerId) {
                let input = prepareUpdateUerData(
                    uerId,
                    submitData,
                    uerRecord?.status,
                    checkboxValues,
                    isUerParsed,
                    currentEstimateBasisData,
                );

                if (isUerParsed && parsedUerData) {
                    const { section, group } = parsedUerData?.data || {};

                    input = { ...input, section, group };
                } else if (
                    uerRecord &&
                    [
                        UerRecordStatusEnum.AdditionalCondition,
                        UerRecordStatusEnum.ContractCondition,
                        UerRecordStatusEnum.NewRecord,
                    ].includes(uerRecord.status)
                ) {
                    const { sectionName: section, groupName: group } = uerRecord;

                    input = { ...input, section, group };
                }

                if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                    dispatch(
                        updateUerToActKC2FromPriceListRequest({
                            workOrderPriceId: Number(workOrderPriceId),
                            input,
                        }),
                    );
                } else if (workOrderPriceId) {
                    dispatch(
                        updateUerToWorkOrderFromPriceListRequest({
                            workOrderPriceId: Number(workOrderPriceId),
                            input,
                        }),
                    );
                } else {
                    dispatch(updateUerPriceListRowRequest({ input }));
                }

                sendGpnAnalyticsEvent({
                    componentId: 'rim_edit price button_pressed',
                    ...analyticsData,
                });
            }
        },

        [
            uerId,
            uerRecord,
            checkboxValues,
            isUerParsed,
            currentEstimateBasisData,
            parsedUerData,
            workOrderStatus,
            sendGpnAnalyticsEvent,
            analyticsData,
            dispatch,
            updateUerToActKC2FromPriceListRequest,
            workOrderPriceId,
            updateUerToWorkOrderFromPriceListRequest,
            updateUerPriceListRowRequest,
        ],
    );

    const onAdd = useCallback(
        (submitData: Record<string, any>) => {
            dispatch(
                createUerRowRequest({
                    input: {
                        ...prepareAddUerData({ formData: submitData, checkboxValues }),
                        contractId,
                    },
                }),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_create price create button_pressed',
                ...analyticsData,
            });
        },
        [dispatch, createUerRowRequest, checkboxValues, contractId, sendGpnAnalyticsEvent, analyticsData],
    );

    const onSubmit = uerId ? onUpdate : onAdd;

    const saveDataReset = uerId
        ? workOrderPriceId
            ? resetUpdateUerToWorkOrderFromPriceList
            : resetUpdateUerPriceListRowHandler
        : resetCreateUerRowHandler;

    const saveStatus = uerId
        ? workOrderPriceId
            ? updateUerToWorkOrderFromPriceListStatus
            : updateUerPriceListRowStatus
        : createUerRowStatus;

    return {
        onSubmit,
        saveStatus:
            workOrderStatus === WorkOrderStatusEnum.InProgress ? updateUerToActKC2FromPriceListStatus : saveStatus,
        saveDataReset,
    };
};
